import { type JSX, useState } from 'react';

import { UcrmAssessmentApi } from 'Api/UCRM/UcrmAssessmentApi';
import { Alert } from 'Components/Alert/Alert';
import { Button } from 'Components/Buttons/Buttons';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { Role } from 'Components/Context/RBACContext';
import { FormFieldUserMultiSelect } from 'Components/FormField/FormFieldUserSelect/FormFieldUserMultiSelect';
import { Modal } from 'Components/Modal/Modal';
import { Text } from 'Components/Text/Text';
import { ICON_CLOSE, ICON_SUBMIT } from 'Config/Icons';
import { UCRM_QUESTIONNAIRE_RESPONDENTS } from 'Config/Tooltips';
import { UcrmDueDiligenceAssessment, UcrmUpdateDueDiligenceAssessmentAssignmentsRequest } from 'Models/Ucrm';

import styles from './InviteRespondentsModal.module.css';

export interface InviteRespondentsModalProps {
    assessmentApi: UcrmAssessmentApi;
    assessments: UcrmDueDiligenceAssessment[];
    hideModal: () => void;
    /** Called after at least one assessment's respondents have been updated, so that the parent can refresh its assessments. */
    onRespondentsUpdated: () => void;
}

interface AlertState {
    message: string;
    type: 'success' | 'error';
}

/**
 * The UCRM counterpart of `InviteContactsModal`. Allows users to add/remove the SummIT users (respondents) who answer each assessment's questionnaire. Used on the Use Case Dashboard.
 * Only assessments whose questionnaires have not been submitted are listed, since respondents have nothing to do after submission.
 *
 * Gotchas:
 * - This uses the same endpoint as `UpdateAssignmentsModal`, so the assessment's owner, delegates, and due dates are sent unchanged alongside the new respondents.
 * - The back-end emails only users who are newly added as respondents; users who were already respondents are not re-emailed.
 * - The back-end replaces an empty respondent list with the use case's manager and delegates.
 */
export const InviteRespondentsModal = (props: InviteRespondentsModalProps): JSX.Element => {
    const { users } = useCachedData();

    const assessmentsAwaitingQuestionnaire = props.assessments.filter((assessment) => !assessment.questionnaire_submitted_timestamp);

    // The respondents last saved for each assessment, used to determine which assessments need to be updated on submit. Updated as each request succeeds so that resubmitting the form only sends changes.
    const [savedRespondentIds, setSavedRespondentIds] = useState<Record<string, string[]>>(() => Object.fromEntries(assessmentsAwaitingQuestionnaire.map((assessment) => [assessment.id, assessment.questionnaire_respondent_ids])));
    const [selectedRespondentIds, setSelectedRespondentIds] = useState<Record<string, string[]>>(savedRespondentIds);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [alertState, setAlertState] = useState<AlertState>();

    const handleSubmit = async (): Promise<void> => {
        const changedAssessments = assessmentsAwaitingQuestionnaire.filter((assessment) => !haveSameMembers(savedRespondentIds[assessment.id], selectedRespondentIds[assessment.id]));
        if (changedAssessments.length === 0) {
            setAlertState({ type: 'success', message: 'Respondents updated.' });
            return;
        }

        setIsSubmitting(true);
        setAlertState(undefined);

        let anyUpdated = false;
        try {
            await Promise.all(
                changedAssessments.map(async (assessment) => {
                    const request: UcrmUpdateDueDiligenceAssessmentAssignmentsRequest = {
                        owner_id: assessment.owner_id,
                        delegate_ids: assessment.delegate_ids,
                        assessment_due_date: assessment.assessment_due_date,
                        questionnaire_due_date: assessment.questionnaire_due_date,
                        questionnaire_respondent_ids: selectedRespondentIds[assessment.id],
                    };
                    const response = await props.assessmentApi.updateAssessmentAssignments(assessment.third_party_id, assessment.service_id, assessment.id, request);
                    anyUpdated = true;

                    // The back-end may substitute default respondents (see the JSDoc above), so the response is the source of truth.
                    setSavedRespondentIds((current) => ({ ...current, [assessment.id]: response.data.questionnaire_respondent_ids }));
                    setSelectedRespondentIds((current) => ({ ...current, [assessment.id]: response.data.questionnaire_respondent_ids }));
                })
            );
            setAlertState({ type: 'success', message: 'Respondents updated.' });
        } catch (error) {
            setAlertState({ type: 'error', message: error.message });
        } finally {
            setIsSubmitting(false);
            if (anyUpdated) {
                props.onRespondentsUpdated();
            }
        }
    };

    return (
        <Modal open header="Invite Respondents">
            {alertState && <Alert type={alertState.type}>{alertState.message}</Alert>}
            {assessmentsAwaitingQuestionnaire.length === 0 ? (
                <Text>{props.assessments.length === 0 ? 'No assessments have been generated. Respondents cannot be invited.' : 'All questionnaires have been submitted. Respondents cannot be invited.'}</Text>
            ) : (
                <>
                    <Text>Select the users to invite to answer each assessment's questionnaire. Newly selected respondents will receive an email with a link to the questionnaire. If no respondents are selected for an assessment, the use case's manager and delegates will be the respondents.</Text>
                    <div className={styles.assessmentFields}>
                        {assessmentsAwaitingQuestionnaire.map((assessment) => (
                            <FormFieldUserMultiSelect key={assessment.id} formFieldId={`${assessment.id}-questionnaireRespondents`} formFieldLabel={`${assessment.name} Respondents`} rolesRestriction={[Role.UCRM_USER]} selectedUsers={users.filter((user) => selectedRespondentIds[assessment.id].includes(user.cognito_subject))} onUsersSelected={(selectedUsers) => setSelectedRespondentIds((current) => ({ ...current, [assessment.id]: (selectedUsers ?? []).map((user) => user.cognito_subject) }))} tooltip={UCRM_QUESTIONNAIRE_RESPONDENTS} />
                        ))}
                    </div>
                </>
            )}
            <div className={styles.buttonContainer}>
                <Button variant="secondary" onClick={props.hideModal} fontAwesomeImage={ICON_CLOSE}>
                    Close
                </Button>
                {assessmentsAwaitingQuestionnaire.length > 0 && (
                    <Button variant="primary" onClick={handleSubmit} fontAwesomeImage={ICON_SUBMIT} isLoading={isSubmitting} loadingText="Inviting...">
                        Invite
                    </Button>
                )}
            </div>
        </Modal>
    );
};

const haveSameMembers = (a: string[], b: string[]): boolean => a.length === b.length && a.every((id) => b.includes(id));
